import React, { useState, useEffect } from 'react';
import { createApiUrl } from '../../config/api';
import {
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Assignment as TaskIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Star as RatingIcon,
  Flag as PriorityIcon,
  Info as StatusIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

const TaskDetail = ({ taskId, onClose, onEdit, onTaskUpdate }) => {
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [completing, setCompleting] = useState(false);
  const [taskStatuses, setTaskStatuses] = useState([]);

  useEffect(() => {
    const fetchTask = async () => {
      try {
        setLoading(true);
        setError('');

        const response = await fetch(createApiUrl(`api/tasks/${taskId}`), {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        const data = await response.json();

        if (response.ok) {
          setTask(data.data);
        } else {
          setError(data.message || 'Failed to fetch task details');
        }
      } catch (error) {
        setError('Network error occurred');
        console.error('Error fetching task:', error);
      } finally {
        setLoading(false);
      }
    };

    if (taskId) {
      fetchTask();
    }
  }, [taskId]);

  // Fetch task statuses to find "Completed" status
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const response = await fetch(createApiUrl('api/tasks/lookup/statuses'));
        const data = await response.json();
        if (response.ok) {
          setTaskStatuses(data.data || []);
        }
      } catch (error) {
        console.error('Error fetching task statuses:', error);
      }
    };
    fetchStatuses();
  }, []);

  const handleMarkComplete = async () => {
    if (!task) return;

    // Find "Completed" status
    const completedStatus = taskStatuses.find(s => 
      s.name.toLowerCase() === 'completed' || s.name.toLowerCase() === 'complete'
    );

    if (!completedStatus) {
      alert('Could not find "Completed" status. Please edit the task manually.');
      return;
    }

    try {
      setCompleting(true);
      const response = await fetch(createApiUrl(`api/tasks/${taskId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          ...task,
          task_status_id: completedStatus.id
        })
      });

      const data = await response.json();

      if (response.ok) {
        setTask(data.data);
        if (onTaskUpdate) {
          onTaskUpdate(data.data);
        }
      } else {
        alert(data.message || 'Failed to mark task as complete');
      }
    } catch (error) {
      console.error('Error marking task as complete:', error);
      alert('Failed to mark task as complete');
    } finally {
      setCompleting(false);
    }
  };

  const isCompleted = task && (task.status_name?.toLowerCase() === 'completed' || task.status_name?.toLowerCase() === 'complete');

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical': return '#d32f2f';
      case 'urgent': return '#f57c00';
      case 'high': return '#ff9800';
      case 'normal': case 'medium': return '#2196f3';
      case 'low': return '#4caf50';
      default: return '#9e9e9e';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return '#4caf50';
      case 'in progress': return '#2196f3';
      case 'not started': return '#9e9e9e';
      case 'on hold': return '#ff9800';
      case 'cancelled': return '#f44336';
      case 'overdue': return '#d32f2f';
      default: return '#9e9e9e';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (startTime, endTime) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end - start;
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <Alert severity="error">{error}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Box>
    );
  }

  if (!task) {
    return (
      <Box>
        <DialogTitle>Task Not Found</DialogTitle>
        <DialogContent>
          <Alert severity="warning">Task not found or has been deleted.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Box>
    );
  }

  return (
    <Box>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TaskIcon />
          <Typography variant="h6" component="span">
            Task Details
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ID: {task.id}
          </Typography>
        </Box>
        <Box>
          <Tooltip title="Edit Task">
            <IconButton onClick={onEdit} sx={{ color: '#ff9800' }}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Task Header */}
        <Box sx={{ p: 3, pb: 2 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            {task.subject}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip
              icon={<StatusIcon />}
              label={task.status_name}
              sx={{ 
                bgcolor: getStatusColor(task.status_name),
                color: 'white',
                fontWeight: 'bold'
              }}
            />
            <Chip
              icon={<PriorityIcon />}
              label={task.priority_name}
              sx={{ 
                bgcolor: getPriorityColor(task.priority_name),
                color: 'white',
                fontWeight: 'bold'
              }}
            />
            <Chip
              icon={<TaskIcon />}
              label={task.type_name}
              variant="outlined"
            />
            {task.rating_name && (
              <Chip
                icon={<RatingIcon />}
                label={task.rating_name}
                variant="outlined"
                sx={{ color: '#ff9800' }}
              />
            )}
          </Box>
        </Box>

        <Divider />

        {/* Task Information Cards */}
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Customer Information */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {task.customer_company_name ? <BusinessIcon /> : <PersonIcon />}
                    Customer Information
                  </Typography>
                  <Box sx={{ ml: 4 }}>
                    <Typography variant="body1" fontWeight="medium">
                      {task.customer_company_name || `${task.customer_first_name} ${task.customer_last_name}`}
                    </Typography>
                    {task.customer_company_name && (
                      <Typography variant="body2" color="text.secondary">
                        Contact: {task.customer_first_name} {task.customer_last_name}
                      </Typography>
                    )}
                    {task.customer_email && (
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                        <EmailIcon fontSize="small" />
                        {task.customer_email}
                      </Typography>
                    )}
                    {task.customer_phone && (
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PhoneIcon fontSize="small" />
                        {task.customer_phone}
                      </Typography>
                    )}
                    {task.customer_mobile && (
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PhoneIcon fontSize="small" />
                        {task.customer_mobile} (Mobile)
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Schedule Information */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ScheduleIcon />
                    Schedule
                  </Typography>
                  <Box sx={{ ml: 4 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Start Time
                    </Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ mb: 2 }}>
                      {formatDate(task.start_time)}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      End Time
                    </Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ mb: 2 }}>
                      {formatDate(task.end_time)}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Duration
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {formatDuration(task.start_time, task.end_time)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Assignment Information */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon />
                    Assignment
                  </Typography>
                  <Box sx={{ ml: 4 }}>
                    {task.assigned_user_name ? (
                      <>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Assigned To
                        </Typography>
                        <Typography variant="body1" fontWeight="medium" sx={{ mb: 2 }}>
                          {task.assigned_user_name}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Not assigned to anyone
                      </Typography>
                    )}
                    
                    {task.sales_person_name ? (
                      <>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Sales Person
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {task.sales_person_name}
                        </Typography>
                        {task.sales_person_email && (
                          <Typography variant="body2" color="text.secondary">
                            {task.sales_person_email}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No sales person assigned
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* System Information */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TaskIcon />
                    System Information
                  </Typography>
                  <Box sx={{ ml: 4 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Created By
                    </Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ mb: 1 }}>
                      {task.created_by_name || 'Unknown'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {formatDate(task.created_at)}
                    </Typography>
                    
                    {task.updated_by_name && (
                      <>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Last Updated By
                        </Typography>
                        <Typography variant="body1" fontWeight="medium" sx={{ mb: 1 }}>
                          {task.updated_by_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(task.updated_at)}
                        </Typography>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Contact Override Information */}
            {(task.email || task.phone_number || task.mobile_phone_number) && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon />
                      Task Contact Info
                    </Typography>
                    <Box sx={{ ml: 4 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Override contact information for this task
                      </Typography>
                      {task.email && (
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <EmailIcon fontSize="small" />
                          {task.email}
                        </Typography>
                      )}
                      {task.phone_number && (
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <PhoneIcon fontSize="small" />
                          {task.phone_number}
                        </Typography>
                      )}
                      {task.mobile_phone_number && (
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PhoneIcon fontSize="small" />
                          {task.mobile_phone_number} (Mobile)
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Task Notes */}
            {task.notes && (
              <Grid size={{ xs: 12 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Notes & Description
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {task.notes}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
        {!isCompleted && (
          <Button 
            variant="contained" 
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={handleMarkComplete}
            disabled={completing}
            sx={{ mr: 1 }}
          >
            {completing ? 'Completing...' : 'Mark Complete'}
          </Button>
        )}
        <Button 
          variant="contained" 
          startIcon={<EditIcon />}
          onClick={onEdit}
          sx={{ bgcolor: '#1976d2' }}
        >
          Edit Task
        </Button>
      </DialogActions>
    </Box>
  );
};

export default TaskDetail; 